// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Collection from "@kaspernj/api-maker/build/collection.js"
import ShowBoxAttribute from "./show-box-attribute.js"

const modelClassData = {
  "attributes": {
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "model_class_name": {
      "column": {
        "default": null,
        "name": "model_class_name",
        "null": false,
        "type": "string"
      },
      "name": "model_class_name",
      "selected_by_default": null,
      "translated": null
    },
    "name": {
      "column": {
        "default": null,
        "name": "name",
        "null": false,
        "type": "string"
      },
      "name": "name",
      "selected_by_default": null,
      "translated": null
    },
    "position": {
      "column": {
        "default": null,
        "name": "position",
        "null": false,
        "type": "integer"
      },
      "name": "position",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "ShowBox",
  "collectionKey": "show_boxes",
  "collectionName": "show_boxes",
  "i18nKey": "show_box",
  "camelizedLower": "showBox",
  "name": "ShowBox",
  "nameDasherized": "show-box",
  "pluralName": "show_boxes",
  "ransackable_associations": [
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "ShowBoxAttribute",
      "collectionName": "show_box_attributes",
      "foreignKey": "show_box_id",
      "name": "show_box_attributes",
      "macro": "has_many",
      "resource_name": "ShowBoxAttribute",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "model_class_name",
      "column": {
        "default": null,
        "name": "model_class_name",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "name",
      "column": {
        "default": null,
        "name": "name",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "position",
      "column": {
        "default": null,
        "name": "position",
        "null": false,
        "type": "integer"
      }
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "ShowBoxAttribute",
      "collectionName": "show_box_attributes",
      "foreignKey": "show_box_id",
      "name": "show_box_attributes",
      "macro": "has_many",
      "resource_name": "ShowBoxAttribute",
      "through": null
    }
  ],
  "paramKey": "show_box",
  "primaryKey": "id"
}

/** Frontend model for ShowBox. */
export default class ShowBox extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string} */
  modelClassName() {
    return this.readAttributeUnderscore("model_class_name")
  }

  /** @returns {boolean} */
  hasModelClassName() {
    const value = this.modelClassName()

    return this._isPresent(value)
  }

  /** @returns {string} */
  name() {
    return this.readAttributeUnderscore("name")
  }

  /** @returns {boolean} */
  hasName() {
    const value = this.name()

    return this._isPresent(value)
  }

  /** @returns {number} */
  position() {
    return this.readAttributeUnderscore("position")
  }

  /** @returns {boolean} */
  hasPosition() {
    const value = this.position()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./show-box-attribute.js").default>} */
  showBoxAttributes() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["show_box_id_eq"] = this.id()

    return new Collection(
      {
        reflectionName: "show_box_attributes",
        model: this,
        modelClass: ShowBoxAttribute
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./show-box-attribute.js").default>>} */
  loadShowBoxAttributes() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["show_box_id_eq"] = this.id()

    return this._loadHasManyReflection(
      {
        reflectionName: "show_box_attributes",
        model: this,
        modelClass: ShowBoxAttribute
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./show-box-attribute.js").default>>} */
  ensureShowBoxAttributesLoaded() {
    return this.ensureAssociationLoaded("show_box_attributes")
  }
}
